import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Coins, Package, Search, Plus, Wallet, ShieldCheck, Tag, Swords, Lock, Sparkles, Wrench, FlaskConical, Dices, Ticket, PackagePlus, Shield, Boxes } from "lucide-react";
import { useState, useMemo } from "react";
import { SectionHeading } from "@/components/Atoms";
import { useAuth } from "@/context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { PackOpeningModal } from "@/components/PackOpeningModal";
import { SingleAcquireModal } from "@/components/SingleAcquireModal";
import { TinyCard, TINY_GRID } from "@/components/Card3D";
import { ClosedLoopNotice } from "@/components/ClosedLoopNotice";
import { PaginationControls } from "@/components/PaginationControls";
import { TopUpCredits } from "@/components/TopUpCredits";
import { benefitOf, RTEXT, RRING, SLOT_META } from "@/components/GearSlots";
import { InventoryIcon, InventoryName } from "@/components/InventoryIdentity";
import { inventoryVisual } from "@/lib/inventoryVisuals";

const RARITIES = ["all", "common", "uncommon", "rare", "epic", "legendary", "goddess"];
const RARITY_RANK = { common: 0, uncommon: 1, rare: 2, epic: 3, legendary: 4, goddess: 5 };

export default function StorePage() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [openingResult, setOpeningResult] = useState(null);
  const [singleAcquired, setSingleAcquired] = useState(null);
  const [search, setSearch] = useState("");
  const [rarity, setRarity] = useState("all");
  const [marketPage, setMarketPage] = useState(0);
  const [marketPageSize, setMarketPageSize] = useState(10);
  const [marketRarity, setMarketRarity] = useState("all");
  const [marketSearch, setMarketSearch] = useState("");
  const [marketSort, setMarketSort] = useState("newest");
  const [tplPage, setTplPage] = useState(0);
  const [tplPageSize, setTplPageSize] = useState(10);
  const [forfeitPage, setForfeitPage] = useState(0);
  const [forfeitPageSize, setForfeitPageSize] = useState(10);
  const [forfeitRarity, setForfeitRarity] = useState("all");
  const [forfeitSearch, setForfeitSearch] = useState("");
  const [forfeitSort, setForfeitSort] = useState("newest");
  const [forfeitItemPage, setForfeitItemPage] = useState(0);
  const [forfeitItemPageSize, setForfeitItemPageSize] = useState(10);
  const [forfeitItemRarity, setForfeitItemRarity] = useState("all");
  const [forfeitItemSearch, setForfeitItemSearch] = useState("");
  const [forfeitItemSort, setForfeitItemSort] = useState("newest");

  const { data: listings, isLoading } = useQuery({
    queryKey: ["listings", user?.id],
    queryFn: async () => (await api.get("/store/listings")).data,
    refetchInterval: 15000,
  });

  const { data: market } = useQuery({
    queryKey: ["market"],
    queryFn: async () => (await api.get("/market")).data,
  });

  const { data: gearMarket } = useQuery({
    queryKey: ["gear-market"],
    queryFn: async () => (await api.get("/gear/market")).data,
    enabled: Boolean(user),
  });
  const [gmRarity, setGmRarity] = useState("all");
  const [gmSearch, setGmSearch] = useState("");
  const [gmSort, setGmSort] = useState("newest");
  const [gmPage, setGmPage] = useState(0);
  const [gmPageSize, setGmPageSize] = useState(10);

  const buyGearMutation = useMutation({
    mutationFn: async (listing_id) => (await api.post(`/gear/${listing_id}/buy`)).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["gear-market"] });
      qc.invalidateQueries({ queryKey: ["adv-state"] });
      refresh();
      toast.success(`${data.gear?.name || "Gear"} (${data.gear?.rarity || ""}) added to your Adventure bag.`);
    },
    onError: (e) => toast.error(apiError(e, "Purchase failed")),
  });
  const onBuyGear = (l) => {
    if (!user) { navigate("/login"); return; }
    if (l.mine || l.seller_id === user.id) { toast.error("That's your own listing."); return; }
    if (user.credits < l.price) { toast.error("Not enough credits — top up to buy."); return; }
    buyGearMutation.mutate(l.id);
  };

  const buyMarketMutation = useMutation({
    mutationFn: async ({ listing_id, currency }) => (await api.post(`/market/${listing_id}/buy`, { currency })).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["market"] });
      qc.invalidateQueries({ queryKey: ["my-cards"] });
      refresh();
      setSingleAcquired(data.card);
    },
    onError: (e) => toast.error(apiError(e, "Purchase failed")),
  });

  const onBuyMarket = (l, currency = "credits") => {
    if (!user) { navigate("/login"); return; }
    if (l.seller_id === user.id) { toast.error("That's your own listing."); return; }
    if (currency === "credits" && user.credits < (l.price || 0)) { toast.error("Not enough credits — redeem a code to top up."); return; }
    // SP balance validated server-side; the UI doesn't cache it here yet.
    buyMarketMutation.mutate({ listing_id: l.id, currency });
  };

  const { data: packLimits } = useQuery({
    queryKey: ["pack-limits"],
    queryFn: async () => (await api.get("/store/pack-limits")).data,
    enabled: !!user,
  });
  const softMax = packLimits?.soft_max ?? 25;
  const maxDiscount = packLimits?.max_discount_pct ?? 25;
  const [packQty, setPackQty] = useState({});
  const qtyOf = (id) => Math.max(1, Math.min(softMax, packQty[id] || 1));
  const discountFor = (qty) => Math.max(0, Math.min(qty, maxDiscount));
  const totalFor = (unit, qty) => Math.round(unit * qty * (1 - discountFor(qty) / 100));
  const setQty = (id, v) => setPackQty((p) => ({ ...p, [id]: Math.max(1, Math.min(softMax, v || 1)) }));

  const buyMutation = useMutation({
    mutationFn: async ({ listing_id, qty }) => (await api.post(`/store/buy/${listing_id}?qty=${qty || 1}`)).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["listings"] });
      qc.invalidateQueries({ queryKey: ["my-cards"] });
      refresh();
      if (data.type === "pack") {
        const cards = data.cards || (data.card ? [data.card] : []);
        setOpeningResult({ cards, packName: data.pack_name, qty: data.qty, discountPct: data.discount_pct });
      } else if (data.type === "item") {
        toast.success(`${data.item?.name || "Item"} claimed — find it in your Adventure bag.`);
      } else if (data.type === "gear") {
        toast.success(`${data.gear?.name || "Gear"} (${data.gear?.rarity || ""}) forged into your Adventure bag.`);
      } else if (data.type === "token") {
        toast.success(`${data.name || "Token"} added to your inventory.`);
      } else {
        setSingleAcquired(data.card);
      }
    },
    onError: (e) => toast.error(apiError(e, "Purchase failed")),
  });

  const buySpMutation = useMutation({
    mutationFn: async (pack_id) => (await api.post(`/packs/${pack_id}/buy-sp`)).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["listings"] });
      qc.invalidateQueries({ queryKey: ["my-cards"] });
      refresh();
      const cards = data.cards || (data.card ? [data.card] : []);
      setOpeningResult({ cards, packName: data.pack_name, qty: data.qty });
      toast.success(`Opened with ${data.paid_sp} SP · ${data.sp_remaining} SP left`);
    },
    onError: (e) => toast.error(apiError(e, "SP purchase failed")),
  });

  const onBuy = (l) => {
    if (!user) { navigate("/login"); return; }
    const qty = qtyOf(l.id);
    const total = totalFor(l.price_credits, qty);
    if (user.credits < total) { toast.error("Not enough credits — redeem a code to top up."); return; }
    buyMutation.mutate({ listing_id: l.id, qty });
  };

  const balance = user?.credits ?? 0;

  const packs = useMemo(
    () => (listings || [])
      .filter((l) => l.type === "pack" && l.pack)
      .filter((l) => !search || l.pack.name.toLowerCase().includes(search.toLowerCase())),
    [listings, search]
  );

  const tokenListings = useMemo(
    () => (listings || []).filter((l) => l.type === "token" && l.token),
    [listings]
  );

  // GEAR CRATES — admin listings of type 'gear'; buying mints a random gear of that rarity.
  const gearCrates = useMemo(
    () => (listings || [])
      .filter((l) => l.type === "gear" && l.gear_crate)
      .filter((l) => rarity === "all" || l.gear_crate.rarity === rarity)
      .sort((a, b) => (RARITY_RANK[a.gear_crate.rarity] ?? 0) - (RARITY_RANK[b.gear_crate.rarity] ?? 0)),
    [listings, rarity]
  );

  // PLAYER GEAR MARKET — gear other collectors listed (/gear/market).
  const gearMarketFiltered = useMemo(() => {
    let arr = (gearMarket || [])
      .filter((l) => l.item)
      .filter((l) => gmRarity === "all" || (l.rarity || l.item.rarity) === gmRarity)
      .filter((l) => !gmSearch || (l.name || l.item.name || "").toLowerCase().includes(gmSearch.toLowerCase()));
    if (gmSort === "price-asc") arr = [...arr].sort((a, b) => a.price - b.price);
    else if (gmSort === "price-desc") arr = [...arr].sort((a, b) => b.price - a.price);
    else if (gmSort === "rarity-desc") arr = [...arr].sort((a, b) => (RARITY_RANK[b.rarity] ?? 0) - (RARITY_RANK[a.rarity] ?? 0));
    return arr;
  }, [gearMarket, gmRarity, gmSearch, gmSort]);
  const safeGmPage = Math.min(gmPage, Math.max(0, Math.ceil(gearMarketFiltered.length / gmPageSize) - 1));
  const pagedGearMarket = gearMarketFiltered.slice(safeGmPage * gmPageSize, (safeGmPage + 1) * gmPageSize);

  // Individual editions: HIDE sold-out (remaining === 0), then apply search + rarity filter.
  const templates = useMemo(
    () => (listings || [])
      .filter((l) => l.type === "template" && l.template)
      .filter((l) => (l.template.remaining ?? 0) > 0)
      .filter((l) => rarity === "all" || l.template.rarity === rarity)
      .filter((l) => !search || (l.template.title || "").toLowerCase().includes(search.toLowerCase())),
    [listings, search, rarity]
  );

  const canAfford = (price) => !user || balance >= price;

  // Forfeited cards: the exact card someone lost/sacrificed, resold by the house.
  // Has its own filters (rarity + search + sort), independent of Store Singles.
  const forfeited = useMemo(() => {
    let arr = (listings || [])
      .filter((l) => l.type === "card" && l.card)
      .filter((l) => forfeitRarity === "all" || l.card.rarity === forfeitRarity)
      .filter((l) => !forfeitSearch || (l.card.title || "").toLowerCase().includes(forfeitSearch.toLowerCase()));
    if (forfeitSort === "price-asc") arr = [...arr].sort((a, b) => a.price_credits - b.price_credits);
    else if (forfeitSort === "price-desc") arr = [...arr].sort((a, b) => b.price_credits - a.price_credits);
    else if (forfeitSort === "rarity-desc") arr = [...arr].sort((a, b) => (RARITY_RANK[b.card.rarity] ?? 0) - (RARITY_RANK[a.card.rarity] ?? 0));
    else if (forfeitSort === "rarity-asc") arr = [...arr].sort((a, b) => (RARITY_RANK[a.card.rarity] ?? 0) - (RARITY_RANK[b.card.rarity] ?? 0));
    return arr; // "newest" keeps the API's created_at-desc order
  }, [listings, forfeitRarity, forfeitSearch, forfeitSort]);
  const safeForfeitPage = Math.min(forfeitPage, Math.max(0, Math.ceil(forfeited.length / forfeitPageSize) - 1));
  const pagedForfeited = forfeited.slice(safeForfeitPage * forfeitPageSize, (safeForfeitPage + 1) * forfeitPageSize);

  // Forfeited ITEMS: gear/consumables stripped off forfeited cards, resold by the house.
  const forfeitedItems = useMemo(() => {
    let arr = (listings || [])
      .filter((l) => l.type === "item" && l.item)
      .filter((l) => forfeitItemRarity === "all" || l.item.rarity === forfeitItemRarity)
      .filter((l) => !forfeitItemSearch || (l.item.name || "").toLowerCase().includes(forfeitItemSearch.toLowerCase()));
    if (forfeitItemSort === "price-asc") arr = [...arr].sort((a, b) => a.price_credits - b.price_credits);
    else if (forfeitItemSort === "price-desc") arr = [...arr].sort((a, b) => b.price_credits - a.price_credits);
    else if (forfeitItemSort === "rarity-desc") arr = [...arr].sort((a, b) => (RARITY_RANK[b.item.rarity] ?? 0) - (RARITY_RANK[a.item.rarity] ?? 0));
    return arr;
  }, [listings, forfeitItemRarity, forfeitItemSearch, forfeitItemSort]);
  const safeForfeitItemPage = Math.min(forfeitItemPage, Math.max(0, Math.ceil(forfeitedItems.length / forfeitItemPageSize) - 1));
  const pagedForfeitedItems = forfeitedItems.slice(safeForfeitItemPage * forfeitItemPageSize, (safeForfeitItemPage + 1) * forfeitItemPageSize);

  const marketItems = useMemo(() => market?.items || [], [market?.items]);
  // Player Market filters: rarity + search (title or seller) + sort.
  const marketFiltered = useMemo(() => {
    let arr = marketItems
      .filter((l) => l.card)
      .filter((l) => marketRarity === "all" || l.card.rarity === marketRarity)
      .filter((l) => !marketSearch || (l.card.title || "").toLowerCase().includes(marketSearch.toLowerCase()) || (l.seller_name || "").toLowerCase().includes(marketSearch.toLowerCase()));
    if (marketSort === "price-asc") arr = [...arr].sort((a, b) => a.price - b.price);
    else if (marketSort === "price-desc") arr = [...arr].sort((a, b) => b.price - a.price);
    else if (marketSort === "rarity-desc") arr = [...arr].sort((a, b) => (RARITY_RANK[b.card.rarity] ?? 0) - (RARITY_RANK[a.card.rarity] ?? 0));
    else if (marketSort === "rarity-asc") arr = [...arr].sort((a, b) => (RARITY_RANK[a.card.rarity] ?? 0) - (RARITY_RANK[b.card.rarity] ?? 0));
    return arr; // "newest" keeps the API order
  }, [marketItems, marketRarity, marketSearch, marketSort]);
  const safeMarketPage = Math.min(marketPage, Math.max(0, Math.ceil(marketFiltered.length / marketPageSize) - 1));
  const pagedMarket = marketFiltered.slice(safeMarketPage * marketPageSize, (safeMarketPage + 1) * marketPageSize);

  const safeTplPage = Math.min(tplPage, Math.max(0, Math.ceil(templates.length / tplPageSize) - 1));
  const pagedTemplates = templates.slice(safeTplPage * tplPageSize, (safeTplPage + 1) * tplPageSize);

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-6 py-5 sm:py-10">
      <SectionHeading
        eyebrow="Acquire"
        title="STORE"
        description="Open randomized packs or claim a specific edition. Sold-out editions are hidden automatically."
      />

      <ClosedLoopNotice variant="card" withBusinessModel className="mb-4 sm:mb-6" testid="store-closed-loop" />

      {/* Wallet + helper bar */}
      <div className="mb-5 sm:mb-8 flex flex-col md:flex-row md:items-center gap-3 sm:gap-4">
        <div className="hud-pill rounded-xl sm:rounded-2xl px-3.5 sm:px-5 py-2.5 sm:py-4 flex items-center gap-3 sm:gap-4 flex-1" data-testid="store-wallet">
          <div className="h-9 w-9 sm:h-11 sm:w-11 rounded-lg sm:rounded-xl bg-amber-400/15 border border-amber-300/30 flex items-center justify-center shrink-0">
            <Wallet className="h-4 w-4 sm:h-5 sm:w-5 text-amber-300" />
          </div>
          <div className="min-w-0">
            <div className="font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.28em] sm:tracking-[0.3em] text-white/40">Your Balance</div>
            <div className="font-display text-xl sm:text-2xl text-white tracking-[0.04em] flex items-center gap-1.5" data-testid="store-balance">
              <Coins className="h-4 w-4 sm:h-5 sm:w-5 text-amber-300" /> {balance.toLocaleString()}
            </div>
          </div>
          <Link to="/redeem" className="ml-auto">
            <Button variant="outline" size="sm" className="border-white/15 text-white" data-testid="store-topup-button">
              <Plus className="h-4 w-4 mr-1" /> Top up
            </Button>
          </Link>
        </div>
        <div className="hud-pill rounded-2xl px-5 py-4 flex items-center gap-3 text-white/55 text-sm md:max-w-xs">
          <ShieldCheck className="h-5 w-5 text-cyan-300 shrink-0" />
          Every purchase mints a brand-new card straight to your Playden.
        </div>
      </div>

      {/* TOP UP CREDITS (Crypto / Venmo) */}
      <div className="mb-14">
        <TopUpCredits />
      </div>

      {/* PLAYER MARKET */}
      <div className="mb-14">
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-display text-2xl text-white tracking-[0.1em] flex items-center gap-2"><Tag className="h-5 w-5 text-cyan-300" /> PLAYER MARKET</h2>
          <span className="font-mono text-[11px] text-white/40 uppercase tracking-[0.2em]">{marketFiltered.length} for sale</span>
        </div>
        <div className="text-[11px] text-white/40 mb-4">Cards listed by other collectors. Hover to preview, click the art to open the card page.</div>

        {/* Player Market filters: rarity + search + sort */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-4">
          <div className="-mx-1 overflow-x-auto lg:overflow-visible">
            <Tabs value={marketRarity} onValueChange={(v) => { setMarketRarity(v); setMarketPage(0); }}>
              <TabsList className="bg-[hsl(220_18%_10%)] border border-white/10 flex flex-wrap h-auto max-w-full">
                {RARITIES.map((r) => (
                  <TabsTrigger key={r} value={r} data-testid={`market-filter-${r}`} className="data-[state=active]:bg-white/10 data-[state=active]:text-white text-white/60 uppercase tracking-[0.2em] text-[10px] whitespace-nowrap">{r}</TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
          <div className="flex items-center gap-3 w-full lg:w-auto lg:ml-auto">
            <div className="relative flex-1 lg:w-56">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
              <Input
                data-testid="market-search-input"
                value={marketSearch}
                onChange={(e) => { setMarketSearch(e.target.value); setMarketPage(0); }}
                placeholder="Search title or seller…"
                className="pl-9 bg-[hsl(220_18%_12%)] border-white/10"
              />
            </div>
            <Select value={marketSort} onValueChange={(v) => { setMarketSort(v); setMarketPage(0); }}>
              <SelectTrigger data-testid="market-sort" className="w-[150px] bg-[hsl(220_18%_12%)] border-white/10 text-white/80 text-xs">
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest" data-testid="market-sort-newest">Newest</SelectItem>
                <SelectItem value="price-asc" data-testid="market-sort-price-asc">Price: Low → High</SelectItem>
                <SelectItem value="price-desc" data-testid="market-sort-price-desc">Price: High → Low</SelectItem>
                <SelectItem value="rarity-desc" data-testid="market-sort-rarity-desc">Rarity: High → Low</SelectItem>
                <SelectItem value="rarity-asc" data-testid="market-sort-rarity-asc">Rarity: Low → High</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {marketFiltered.length === 0 ? (
          <div className="hud-pill rounded-2xl p-8 text-center text-white/50">{marketItems.length === 0 ? "No player listings right now. List your own from any card's detail page." : "No listings match your filters."}</div>
        ) : (
          <>
            <div className={TINY_GRID}>
              {pagedMarket.map((l) => {
                const c = l.card;
                const isMine = user && l.seller_id === user.id;
                const hasCredits = (l.price || 0) > 0;
                const hasSp = (l.price_sp || 0) > 0;
                const affordable = !user || user.credits >= (l.price || 0);
                return (
                  <div key={l.id} data-testid={`market-listing-${l.id}`}>
                    <TinyCard
                      card={c}
                      onClick={() => navigate(`/card/${c.id}`)}
                      footer={
                        <div className="mt-1 px-0.5 space-y-1">
                          <div className="font-mono text-[9px] text-white/45 truncate">{c.title || "Card"} · {l.seller_name}</div>
                          <div className="flex items-center justify-between gap-1 flex-wrap">
                            <div className="flex items-center gap-1 flex-wrap">
                              {hasCredits && <span className="flex items-center gap-0.5 font-mono text-[10px] text-amber-300"><Coins className="h-3 w-3" /> {l.price.toLocaleString()}</span>}
                              {hasSp && <span data-testid={`market-price-sp-${l.id}`} className="flex items-center gap-0.5 font-mono text-[10px] text-fuchsia-300">✦ {l.price_sp.toLocaleString()} SP</span>}
                            </div>
                            <div className="flex gap-1">
                              {hasCredits && (
                                <Button
                                  data-testid={`market-buy-button-${l.id}`}
                                  onClick={() => onBuyMarket(l, "credits")}
                                  disabled={buyMarketMutation.isPending || isMine || !affordable}
                                  size="sm"
                                  className="h-6 px-2 text-[10px] bg-cyan-400 text-black hover:bg-cyan-300 disabled:opacity-50"
                                >
                                  {isMine ? "Yours" : affordable ? "Buy" : "Low"}
                                </Button>
                              )}
                              {hasSp && (
                                <Button
                                  data-testid={`market-buy-sp-button-${l.id}`}
                                  onClick={() => onBuyMarket(l, "sp")}
                                  disabled={buyMarketMutation.isPending || isMine}
                                  size="sm"
                                  className="h-6 px-2 text-[10px] bg-fuchsia-400 text-black hover:bg-fuchsia-300 disabled:opacity-50"
                                >
                                  {isMine ? "" : "SP"}
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      }
                    />
                  </div>
                );
              })}
            </div>
            <PaginationControls
              page={safeMarketPage}
              total={marketFiltered.length}
              pageSize={marketPageSize}
              onPageChange={setMarketPage}
              onPageSizeChange={(s) => { setMarketPageSize(s); setMarketPage(0); }}
              testidPrefix="market"
            />
          </>
        )}
      </div>

      {/* STORE SINGLES — unminted card editions listed by admins, priced by rarity */}
      {templates.length > 0 && (
        <div className="mb-14" data-testid="store-singles-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-2xl text-white tracking-[0.1em] flex items-center gap-2"><Sparkles className="h-5 w-5 text-amber-300" /> STORE SINGLES</h2>
            <span className="font-mono text-[11px] text-white/40 uppercase tracking-[0.2em]">{templates.length} for sale</span>
          </div>
          <div className="text-[11px] text-white/40 mb-4">Brand-new card editions from the house. Each purchase mints a fresh copy straight to your Playden.</div>
          <div className={TINY_GRID}>
            {pagedTemplates.map((l) => {
              const t = l.template;
              const affordable = canAfford(l.price_credits);
              const discounted = l.discount_type && l.base_price && l.base_price !== l.price_credits;
              return (
                <div key={l.id} data-testid={`store-single-${l.id}`}>
                  <TinyCard
                    card={t}
                    onClick={() => t.latest_card_id && navigate(`/card/${t.latest_card_id}`)}
                    footer={
                      <div className="mt-1 px-0.5 space-y-1">
                        <div className="font-mono text-[9px] text-white/55 truncate">{t.title || "Card"} · {t.remaining} left</div>
                        {discounted && (
                          <div className="inline-flex items-center gap-1 font-mono text-[9px] text-emerald-300" data-testid={`single-discount-${l.id}`}>
                            <Tag className="h-2.5 w-2.5" /> {l.discount_type === "pct" ? `${l.discount_value}% off` : `${l.discount_value}c off`}
                          </div>
                        )}
                        <div className="flex items-center justify-between gap-1">
                          <span className="flex items-center gap-0.5 font-mono text-[10px] text-amber-300" data-testid={`single-price-${l.id}`}>
                            <Coins className="h-3 w-3" />
                            {discounted && <span className="line-through text-white/30 mr-1">{l.base_price.toLocaleString()}</span>}
                            {l.price_credits.toLocaleString()}
                          </span>
                          <Button
                            data-testid={`store-buy-single-button-${l.id}`}
                            onClick={() => onBuy(l)}
                            disabled={buyMutation.isPending || !affordable}
                            size="sm"
                            className="h-6 px-2 text-[10px] bg-amber-400 text-black hover:bg-amber-300 disabled:opacity-50"
                          >
                            {affordable ? "Buy" : "Low"}
                          </Button>
                        </div>
                      </div>
                    }
                  />
                </div>
              );
            })}
          </div>
          <PaginationControls
            page={safeTplPage}
            total={templates.length}
            pageSize={tplPageSize}
            onPageChange={setTplPage}
            onPageSizeChange={(s) => { setTplPageSize(s); setTplPage(0); }}
            testidPrefix="singles"
          />
        </div>
      )}


      {/* TOKENS — consumable tokens (reroll, arena, etc.) buyable with credits */}
      {tokenListings.length > 0 && (
        <div className="mb-14" data-testid="store-tokens-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-2xl text-white tracking-[0.1em] flex items-center gap-2"><Dices className="h-5 w-5 text-fuchsia-300" /> TOKENS</h2>
            <span className="font-mono text-[11px] text-white/40 uppercase tracking-[0.2em]">{tokenListings.length} available</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {tokenListings.map((l) => {
              const col = inventoryVisual(l.token).color;
              const affordable = canAfford(l.price_credits);
              return (
                <div key={l.id} className="hud-pill rounded-2xl overflow-hidden flex flex-col hover:scale-[1.01] transition-transform duration-200" data-testid={`store-token-${l.token.type}`}>
                  <div className="p-4 flex items-start gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl shrink-0" style={{ background: `${col}22`, border: `1px solid ${col}66` }}>
                      <InventoryIcon item={l.token} className="h-6 w-6" />
                    </div>
                    <div className="min-w-0">
                      <InventoryName item={l.token} className="block text-lg" />
                      <p className="text-sm text-white/55 line-clamp-3 mt-0.5">{l.token.desc}</p>
                    </div>
                  </div>
                  <div className="px-4 pb-4 mt-auto flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 font-mono text-sm text-amber-300" data-testid={`token-price-${l.token.type}`}>
                      <Coins className="h-4 w-4" /> {l.price_credits.toLocaleString()}
                    </div>
                    <Button
                      data-testid={`store-buy-token-button-${l.token.type}`}
                      onClick={() => onBuy(l)}
                      disabled={buyMutation.isPending || !affordable}
                      className="text-black hover:opacity-90 disabled:opacity-50"
                      style={{ background: col }}
                    >
                      {buyMutation.isPending ? "Buying…" : affordable ? "Buy 1" : "Need more"}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* GEAR CRATES — admin listings; buying mints a random gear of that rarity */}
      {gearCrates.length > 0 && (
        <div className="mb-14" data-testid="store-gear-crates-section">
          <div className="flex items-center justify-between mb-1">
            <h2 className="font-display text-2xl text-white tracking-[0.1em] flex items-center gap-2"><Boxes className="h-5 w-5 text-cyan-300" /> GEAR CRATES</h2>
            <span className="font-mono text-[11px] text-white/40 uppercase tracking-[0.2em]">{gearCrates.length} crates</span>
          </div>
          <div className="text-[11px] text-white/40 mb-4">Buy a sealed crate and the house forges a random piece of gear at that rarity straight into your Adventure bag.</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {gearCrates.map((l) => {
              const rar = l.gear_crate.rarity;
              const affordable = canAfford(l.price_credits);
              const discounted = l.discount_type && l.base_price && l.base_price !== l.price_credits;
              return (
                <div key={l.id} data-testid={`store-gear-crate-${l.id}`} className={`rounded-xl border bg-black/30 p-3 flex flex-col gap-2 ${RRING[rar] || "border-white/10"}`}>
                  <div className="flex items-start gap-2">
                    <div className={`shrink-0 grid place-items-center h-10 w-10 rounded-lg bg-white/[0.04] border ${RRING[rar] || "border-white/10"}`}>
                      <Shield className={`h-5 w-5 ${RTEXT[rar] || "text-white/70"}`} />
                    </div>
                    <div className="min-w-0">
                      <InventoryName item={{ ...l.gear_crate, rarity: rar }} className="block text-sm" />
                      <div className="font-mono text-[9px] uppercase tracking-widest text-white/40">{rar} · random gear</div>
                    </div>
                  </div>
                  {discounted && (
                    <div className="inline-flex items-center gap-1 font-mono text-[9px] text-emerald-300" data-testid={`gear-crate-discount-${l.id}`}>
                      <Tag className="h-2.5 w-2.5" /> {l.discount_type === "pct" ? `${l.discount_value}% off` : `${l.discount_value}c off`}
                    </div>
                  )}
                  <div className="mt-auto flex items-center justify-between gap-1">
                    <span className="flex items-center gap-0.5 font-mono text-[11px] text-amber-300" data-testid={`gear-crate-price-${l.id}`}>
                      <Coins className="h-3 w-3" />
                      {discounted && <span className="line-through text-white/30 mr-1">{l.base_price.toLocaleString()}</span>}
                      {l.price_credits.toLocaleString()}
                    </span>
                    <Button
                      data-testid={`store-buy-gear-crate-button-${l.id}`}
                      onClick={() => onBuy(l)}
                      disabled={buyMutation.isPending || !affordable}
                      size="sm"
                      className="h-6 px-2.5 text-[10px] bg-cyan-400 text-black hover:bg-cyan-300 disabled:opacity-50"
                    >
                      {affordable ? "Open" : "Low"}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* PLAYER GEAR MARKET — gear listed by other collectors */}
      <div className="mb-14" data-testid="store-gear-market-section">
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-display text-2xl text-white tracking-[0.1em] flex items-center gap-2"><Wrench className="h-5 w-5 text-emerald-300" /> PLAYER GEAR MARKET</h2>
          <span className="font-mono text-[11px] text-white/40 uppercase tracking-[0.2em]">{gearMarketFiltered.length} for sale</span>
        </div>
        <div className="text-[11px] text-white/40 mb-4">Gear listed by other collectors. List your own from the Adventure bag — a {Math.round(0.10 * 100)}% house fee applies on sale.</div>
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <Tabs value={gmRarity} onValueChange={(v) => { setGmRarity(v); setGmPage(0); }}>
            <TabsList className="bg-[hsl(220_18%_10%)] border border-white/10 flex-wrap h-auto">
              {RARITIES.map((r) => (
                <TabsTrigger key={r} value={r} data-testid={`gear-market-filter-${r}`} className="data-[state=active]:bg-white/10 data-[state=active]:text-white text-white/60 uppercase tracking-[0.2em] text-[10px] whitespace-nowrap">{r}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/30" />
            <Input data-testid="gear-market-search-input" value={gmSearch} onChange={(e) => { setGmSearch(e.target.value); setGmPage(0); }} placeholder="Search gear…" className="pl-8 h-9 w-44 bg-[hsl(220_18%_12%)] border-white/10 text-white/80 text-xs" />
          </div>
          <Select value={gmSort} onValueChange={(v) => { setGmSort(v); setGmPage(0); }}>
            <SelectTrigger data-testid="gear-market-sort" className="w-[150px] bg-[hsl(220_18%_12%)] border-white/10 text-white/80 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[hsl(220_18%_12%)] border-white/10 text-white/80">
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="price-asc">Price: Low → High</SelectItem>
              <SelectItem value="price-desc">Price: High → Low</SelectItem>
              <SelectItem value="rarity-desc">Rarity: High → Low</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {gearMarketFiltered.length === 0 ? (
          <div className="hud-pill rounded-2xl p-8 text-center text-white/50" data-testid="store-gear-market-empty">{!user ? <Link to="/login" data-testid="store-gear-sign-in" className="text-cyan-300 hover:text-cyan-200">Sign in to browse player gear</Link> : (gearMarket || []).length === 0 ? "No gear listed by players right now. List your own from the Adventure bag." : "No gear matches your filters."}</div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {pagedGearMarket.map((l) => {
                const it = l.item;
                const rar = l.rarity || it.rarity;
                const isMine = l.mine || (user && l.seller_id === user.id);
                const affordable = !user || user.credits >= l.price;
                const SlotIcon = (SLOT_META[it.slot]?.icon) || Wrench;
                return (
                  <div key={l.id} data-testid={`gear-market-listing-${l.id}`} className={`rounded-xl border bg-black/30 p-3 flex flex-col gap-2 ${RRING[rar] || "border-white/10"}`}>
                    <div className="flex items-start gap-2">
                      <div className={`shrink-0 grid place-items-center h-9 w-9 rounded-lg bg-white/[0.04] border ${RRING[rar] || "border-white/10"}`}>
                        <InventoryIcon item={it} />
                      </div>
                      <div className="min-w-0">
                        <InventoryName item={it} className="block text-[13px] truncate" />
                        <div className="font-mono text-[9px] uppercase tracking-widest text-white/40">{rar}{it.slot ? ` · ${it.slot}` : ""}{it.tier ? ` · T${it.tier}` : ""}</div>
                      </div>
                    </div>
                    <div className="font-mono text-[11px] text-white/65 leading-snug min-h-[2.2em]">{benefitOf(it)}</div>
                    <div className="mt-auto flex items-center justify-between gap-1">
                      <span className="flex items-center gap-0.5 font-mono text-[11px] text-amber-300"><Coins className="h-3 w-3" /> {l.price.toLocaleString()}</span>
                      <Button
                        data-testid={`gear-market-buy-button-${l.id}`}
                        onClick={() => onBuyGear(l)}
                        disabled={buyGearMutation.isPending || isMine || !affordable}
                        size="sm"
                        className="h-6 px-2.5 text-[10px] bg-emerald-400 text-black hover:bg-emerald-300 disabled:opacity-50"
                      >
                        {isMine ? "Yours" : affordable ? "Buy" : "Low"}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
            <PaginationControls
              page={safeGmPage}
              total={gearMarketFiltered.length}
              pageSize={gmPageSize}
              onPageChange={setGmPage}
              onPageSizeChange={(s) => { setGmPageSize(s); setGmPage(0); }}
              testidPrefix="gear-market"
            />
          </>
        )}
      </div>

      {/* PACKS */}
      <div className="mb-14">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-2xl text-white tracking-[0.1em]">PACKS</h2>
          <span className="font-mono text-[11px] text-white/40 uppercase tracking-[0.2em]">{packs.length} available</span>
        </div>
        {packs.length === 0 ? (
          <div className="hud-pill rounded-2xl p-8 text-center text-white/50">{search ? "No packs match your search." : "No packs listed yet."}</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {packs.map((l) => {
              const affordable = canAfford(l.price_credits);
              return (
                <div key={l.id} className="hud-pill rounded-2xl overflow-hidden group hover:scale-[1.01] transition-transform duration-200 flex flex-col">
                  <div className="relative aspect-[3/2] bg-gradient-to-br from-cyan-900/40 via-violet-900/30 to-amber-900/30 overflow-hidden">
                    {l.pack.image ? (
                      <img src={l.pack.image} alt={l.pack.name} className="absolute inset-0 w-full h-full object-cover" />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Package className="h-16 w-16 text-white/40" />
                      </div>
                    )}
                    <div className="absolute inset-0 holo-scan" />
                    <div className="absolute bottom-3 left-3 right-3">
                      <div className="font-display text-2xl text-white tracking-[0.06em] drop-shadow-[0_2px_8px_rgba(0,0,0,0.85)]">{l.pack.name}</div>
                    </div>
                  </div>
                  <div className="p-4 flex flex-col flex-1">
                    <p className="text-sm text-white/60 line-clamp-2 min-h-[40px]">{l.pack.description || "A randomized pack with weighted rarity odds."}</p>
                    <div data-testid={`pack-card-count-${l.id}`} className="mt-3 mb-2 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-cyan-200">
                      <span data-testid={`pack-card-number-${l.id}`} className="font-display text-6xl leading-none tracking-normal">{l.pack.cards_per_pack || 1}</span>
                      <span className="font-display text-2xl sm:text-3xl leading-none tracking-normal">{(l.pack.cards_per_pack || 1) === 1 ? "CARD" : "CARDS"} PER PACK</span>
                    </div>
                    {/* Odds */}
                    <div className="mt-3 grid grid-cols-3 gap-1">
                      {Object.entries(l.pack.odds || {}).map(([r, p]) => (
                        <div key={r} className={`rarity-${r} text-center px-1.5 py-1 rounded`} style={{ background: "rgba(0,0,0,0.4)", border: "1px solid var(--edge)" }}>
                          <div className="font-mono text-[8px] uppercase tracking-[0.2em]" style={{ color: "var(--label)" }}>{r}</div>
                          <div className="font-mono text-[10px] text-white/70">{(p * 100).toFixed(1)}%</div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 space-y-2.5">
                      {(() => {
                        const qty = qtyOf(l.id);
                        const disc = discountFor(qty);
                        const total = totalFor(l.price_credits, qty);
                        const affordableQty = canAfford(total);
                        return (
                          <>
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-1.5 font-mono text-sm text-amber-300" data-testid={`pack-price-${l.pack.id}`}>
                                <Coins className="h-4 w-4" /> {l.price_credits.toLocaleString()}
                                <span className="text-white/30 text-[10px]">/ pack</span>
                              </div>
                              <div className="flex items-center gap-1" data-testid={`pack-qty-stepper-${l.id}`}>
                                <button onClick={() => setQty(l.id, qty - 1)} disabled={qty <= 1} className="h-7 w-7 rounded-md border border-white/15 text-white/80 hover:bg-white/10 disabled:opacity-30 leading-none">−</button>
                                <input
                                  data-testid={`pack-qty-input-${l.id}`}
                                  type="number" min={1} max={softMax} value={qty}
                                  onChange={(e) => setQty(l.id, parseInt(e.target.value))}
                                  className="w-12 h-7 text-center bg-black/40 border border-white/10 rounded-md text-white text-sm font-mono"
                                />
                                <button onClick={() => setQty(l.id, qty + 1)} disabled={qty >= softMax} className="h-7 w-7 rounded-md border border-white/15 text-white/80 hover:bg-white/10 disabled:opacity-30 leading-none">+</button>
                              </div>
                            </div>
                            <div className="flex items-center justify-between gap-2">
                              <div className="text-[11px] font-mono" data-testid={`pack-total-${l.id}`}>
                                <span className="text-white/50">Total </span>
                                <span className="text-amber-200">{total.toLocaleString()}c</span>
                                {disc > 0 && <span className="ml-1.5 px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-400/30" data-testid={`pack-discount-${l.id}`}>−{disc}%</span>}
                              </div>
                              <Button
                                data-testid={`store-buy-pack-button-${l.id}`}
                                onClick={() => onBuy(l)}
                                disabled={buyMutation.isPending || !affordableQty}
                                className="bg-cyan-400 text-black hover:bg-cyan-300 disabled:opacity-50"
                              >
                                {buyMutation.isPending ? "Opening…" : affordableQty ? (qty > 1 ? `Open ${qty}` : "Open Pack") : "Need more"}
                              </Button>
                            </div>
                            {qty < softMax && disc < maxDiscount && (
                              <div className="text-[9px] text-white/35">Buy {Math.min(softMax, maxDiscount)} for {Math.min(softMax, maxDiscount)}% off · bulk discount stacks 1% per pack.</div>
                            )}
                            {l.pack.price_sp > 0 && (
                              <Button
                                data-testid={`store-buy-pack-sp-button-${l.id}`}
                                onClick={() => { if (!user) { navigate("/login"); return; } buySpMutation.mutate(l.pack.id); }}
                                disabled={buySpMutation.isPending}
                                variant="outline"
                                className="w-full border-yellow-300/40 text-yellow-200 hover:bg-yellow-300/10 disabled:opacity-50"
                              >
                                {buySpMutation.isPending ? "Opening…" : `Buy 1 with ${l.pack.price_sp} SP`}
                              </Button>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* FORFEITED CARDS — exact cards lost/sacrificed, resold by the house */}
      <div className="mb-14">
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-display text-2xl text-white tracking-[0.1em] flex items-center gap-2"><Swords className="h-5 w-5 text-rose-300" /> FORFEITED CARDS</h2>
          <span className="font-mono text-[11px] text-white/40 uppercase tracking-[0.2em]">{forfeited.length} for sale</span>
        </div>

        {/* Forfeit vs Unminted — what happens when a card leaves a
            collector's hands. Two very different outcomes: it either
            goes on sale here (Forfeited) or is erased entirely (Unminted). */}
        <div className="grid sm:grid-cols-2 gap-3 mb-4" data-testid="forfeit-vs-unminted">
          <div className="rounded-xl border border-rose-400/30 bg-rose-500/[0.06] p-3">
            <div className="flex items-center gap-1.5 mb-1.5">
              <Swords className="h-3.5 w-3.5 text-rose-300 shrink-0" />
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-rose-200 font-semibold">Forfeited (the cards on sale below)</span>
            </div>
            <p className="text-[11px] text-white/70 leading-relaxed">
              The card was <span className="text-white">lost in battle</span> — an Arena defeat, a Cavern wipe, a Gauntlet crash, or given up in a Forge sacrifice. The card still <span className="text-white font-medium">exists as a specific edition</span> (same serial #, same XP/level, same battle scars) — the house just owns it now and re-lists it here at rarity × 1.66. Buy it and that exact card is yours, history and all.
            </p>
          </div>
          <div className="rounded-xl border border-slate-400/30 bg-slate-500/[0.06] p-3">
            <div className="flex items-center gap-1.5 mb-1.5">
              <FlaskConical className="h-3.5 w-3.5 text-slate-300 shrink-0" />
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-200 font-semibold">Unminted (destroyed — not for sale)</span>
            </div>
            <p className="text-[11px] text-white/70 leading-relaxed">
              The card was <span className="text-white">erased from existence</span> — most commonly via <span className="text-white font-medium">sellback</span>, where the collector traded the card back to the pool for 1/10 its base value in credits. Serial # is retired, all XP and stats gone. Unminted cards never appear in this storefront — their edition slot is returned to the pack pool for someone else to pull fresh.
            </p>
          </div>
        </div>

        {/* Forfeited filters: rarity + search + sort (independent of Store Singles) */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-4">
          <div className="-mx-1 overflow-x-auto lg:overflow-visible">
            <Tabs value={forfeitRarity} onValueChange={(v) => { setForfeitRarity(v); setForfeitPage(0); }}>
              <TabsList className="bg-[hsl(220_18%_10%)] border border-white/10 flex flex-wrap h-auto max-w-full">
                {RARITIES.map((r) => (
                  <TabsTrigger key={r} value={r} data-testid={`forfeited-filter-${r}`} className="data-[state=active]:bg-white/10 data-[state=active]:text-white text-white/60 uppercase tracking-[0.2em] text-[10px] whitespace-nowrap">{r}</TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
          <div className="flex items-center gap-3 w-full lg:w-auto lg:ml-auto">
            <div className="relative flex-1 lg:w-56">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
              <Input
                data-testid="forfeited-search-input"
                value={forfeitSearch}
                onChange={(e) => { setForfeitSearch(e.target.value); setForfeitPage(0); }}
                placeholder="Search forfeited…"
                className="pl-9 bg-[hsl(220_18%_12%)] border-white/10"
              />
            </div>
            <Select value={forfeitSort} onValueChange={(v) => { setForfeitSort(v); setForfeitPage(0); }}>
              <SelectTrigger data-testid="forfeited-sort" className="w-[150px] bg-[hsl(220_18%_12%)] border-white/10 text-white/80 text-xs">
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest" data-testid="forfeited-sort-newest">Newest</SelectItem>
                <SelectItem value="price-asc" data-testid="forfeited-sort-price-asc">Price: Low → High</SelectItem>
                <SelectItem value="price-desc" data-testid="forfeited-sort-price-desc">Price: High → Low</SelectItem>
                <SelectItem value="rarity-desc" data-testid="forfeited-sort-rarity-desc">Rarity: High → Low</SelectItem>
                <SelectItem value="rarity-asc" data-testid="forfeited-sort-rarity-asc">Rarity: Low → High</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {forfeited.length === 0 ? (
          <div className="hud-pill rounded-2xl p-8 text-center text-white/50" data-testid="store-forfeited-empty">{(forfeitSearch || forfeitRarity !== "all") ? "No forfeited cards match your filters." : "No forfeited cards on the block right now. Win battles to send rivals' cards here."}</div>
        ) : (
          <>
            <div className={TINY_GRID}>
              {pagedForfeited.map((l) => {
                const c = l.card;
                const affordable = canAfford(l.price_credits);
                const reserved = l.buyback_active && l.buyback_user_id && user?.id !== l.buyback_user_id;
                const myReclaim = l.buyback_active && l.buyback_user_id && user?.id === l.buyback_user_id;
                const reservedMins = Math.max(1, Math.round((l.buyback_seconds_left || 0) / 60));
                return (
                  <div key={l.id} data-testid={`store-forfeited-${l.id}`}>
                    <TinyCard
                      card={c}
                      onClick={() => navigate(`/card/${c.id}`)}
                      footer={
                        <div className="mt-1 px-0.5 space-y-1">
                          <div className="font-mono text-[9px] text-white/60 truncate">{c.title || "Card"}{c.edition_number ? ` · #${c.edition_number}` : ""}</div>
                          {(c.evolution > 0) && (
                            <div className="inline-flex items-center gap-0.5 font-mono text-[9px] text-fuchsia-300" data-testid={`store-evolution-${l.id}`}><Sparkles className="h-2.5 w-2.5" /> Evo {c.evolution} · Lv {c.level ?? 1}</div>
                          )}
                          {reserved && (
                            <div className="inline-flex items-center gap-0.5 font-mono text-[9px] text-amber-300" data-testid={`store-buyback-${l.id}`}><Lock className="h-2.5 w-2.5" /> Owner priority · {reservedMins}m</div>
                          )}
                          {myReclaim && (
                            <div className="inline-flex items-center gap-0.5 font-mono text-[9px] text-emerald-300" data-testid={`store-reclaim-${l.id}`}><Sparkles className="h-2.5 w-2.5" /> Yours to reclaim · {reservedMins}m left</div>
                          )}
                          <div className="flex items-center justify-between gap-1">
                            <span className="flex items-center gap-0.5 font-mono text-[10px] text-amber-300"><Coins className="h-3 w-3" /> {l.price_credits.toLocaleString()}</span>
                            <Button
                              data-testid={`store-buy-forfeited-button-${l.id}`}
                              onClick={() => onBuy(l)}
                              disabled={buyMutation.isPending || !affordable || reserved}
                              size="sm"
                              className="h-6 px-2 text-[10px] bg-rose-400 text-black hover:bg-rose-300 disabled:opacity-50"
                            >
                              {reserved ? "Held" : affordable ? "Claim" : "Low"}
                            </Button>
                          </div>
                        </div>
                      }
                    />
                  </div>
                );
              })}
            </div>
            <PaginationControls
              page={safeForfeitPage}
              total={forfeited.length}
              pageSize={forfeitPageSize}
              onPageChange={setForfeitPage}
              onPageSizeChange={(s) => { setForfeitPageSize(s); setForfeitPage(0); }}
              testidPrefix="store-forfeited"
            />
          </>
        )}
      </div>

      {/* Forfeited GEAR & CONSUMABLES — stripped off forfeited cards, resold by the house */}
      <div className="mt-12" data-testid="store-forfeited-items-section">
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-display text-2xl text-white tracking-[0.1em] flex items-center gap-2"><Wrench className="h-5 w-5 text-emerald-300" /> FORFEITED GEAR & ITEMS</h2>
          <span className="font-mono text-[11px] text-white/40 uppercase tracking-[0.2em]">{forfeitedItems.length} for sale</span>
        </div>
        <div className="text-[11px] text-white/40 mb-4">Gear &amp; consumables stripped off forfeited cards — priced by their rolled effects &amp; rarity. Claim one and it drops straight into your Adventure bag.</div>
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <Tabs value={forfeitItemRarity} onValueChange={(v) => { setForfeitItemRarity(v); setForfeitItemPage(0); }}>
            <TabsList className="bg-[hsl(220_18%_10%)] border border-white/10 flex-wrap h-auto">
              {RARITIES.map((r) => (
                <TabsTrigger key={r} value={r} data-testid={`forfeited-item-filter-${r}`} className="data-[state=active]:bg-white/10 data-[state=active]:text-white text-white/60 uppercase tracking-[0.2em] text-[10px] whitespace-nowrap">{r}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/30" />
            <Input data-testid="forfeited-item-search-input" value={forfeitItemSearch} onChange={(e) => { setForfeitItemSearch(e.target.value); setForfeitItemPage(0); }} placeholder="Search items…" className="pl-8 h-9 w-44 bg-[hsl(220_18%_12%)] border-white/10 text-white/80 text-xs" />
          </div>
          <Select value={forfeitItemSort} onValueChange={(v) => { setForfeitItemSort(v); setForfeitItemPage(0); }}>
            <SelectTrigger data-testid="forfeited-item-sort" className="w-[150px] bg-[hsl(220_18%_12%)] border-white/10 text-white/80 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[hsl(220_18%_12%)] border-white/10 text-white/80">
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="price-asc">Price: Low → High</SelectItem>
              <SelectItem value="price-desc">Price: High → Low</SelectItem>
              <SelectItem value="rarity-desc">Rarity: High → Low</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {forfeitedItems.length === 0 ? (
          <div className="hud-pill rounded-2xl p-8 text-center text-white/50" data-testid="store-forfeited-items-empty">No forfeited gear right now. Beat a rival whose card is carrying gear to send it here.</div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {pagedForfeitedItems.map((l) => {
                const it = l.item;
                const affordable = canAfford(l.price_credits);
                const myReclaim = l.buyback_active && l.buyback_user_id && user?.id === l.buyback_user_id;
                const reservedMins = Math.max(1, Math.round((l.buyback_seconds_left || 0) / 60));
                const SlotIcon = (SLOT_META[it.slot]?.icon) || (it.kind === "consumable" ? FlaskConical : Wrench);
                return (
                  <div key={l.id} data-testid={`store-forfeited-item-${l.id}`} className={`rounded-xl border ${myReclaim ? "border-emerald-400/50" : (RRING[it.rarity] || "border-white/10")} bg-black/30 p-3 flex flex-col gap-2`}>
                    <div className="flex items-start gap-2">
                      <div className={`shrink-0 grid place-items-center h-9 w-9 rounded-lg bg-white/[0.04] border ${RRING[it.rarity] || "border-white/10"}`}>
                        <InventoryIcon item={it} />
                      </div>
                      <div className="min-w-0">
                        <InventoryName item={it} className="block text-[13px] truncate" />
                        <div className="font-mono text-[9px] uppercase tracking-widest text-white/40">{it.rarity}{it.slot ? ` · ${it.slot}` : it.kind ? ` · ${it.kind}` : ""}{it.tier ? ` · T${it.tier}` : ""}</div>
                      </div>
                    </div>
                    <div className="font-mono text-[11px] text-white/65 leading-snug min-h-[2.2em]">{benefitOf(it)}</div>
                    {myReclaim && (
                      <div className="inline-flex items-center gap-0.5 font-mono text-[9px] text-emerald-300" data-testid={`store-item-reclaim-${l.id}`}><Sparkles className="h-2.5 w-2.5" /> Yours to reclaim · {reservedMins}m left</div>
                    )}
                    <div className="mt-auto flex items-center justify-between gap-1">
                      <span className="flex items-center gap-0.5 font-mono text-[11px] text-amber-300"><Coins className="h-3 w-3" /> {l.price_credits.toLocaleString()}</span>
                      <Button
                        data-testid={`store-buy-forfeited-item-button-${l.id}`}
                        onClick={() => onBuy(l)}
                        disabled={buyMutation.isPending || !affordable}
                        size="sm"
                        className={`h-6 px-2.5 text-[10px] text-black disabled:opacity-50 ${myReclaim ? "bg-emerald-400 hover:bg-emerald-300" : "bg-emerald-400 hover:bg-emerald-300"}`}
                      >
                        {myReclaim ? "Reclaim" : affordable ? "Claim" : "Low"}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
            <PaginationControls
              page={safeForfeitItemPage}
              total={forfeitedItems.length}
              pageSize={forfeitItemPageSize}
              onPageChange={setForfeitItemPage}
              onPageSizeChange={(s) => { setForfeitItemPageSize(s); setForfeitItemPage(0); }}
              testidPrefix="store-forfeited-items"
            />
          </>
        )}
      </div>

      {openingResult && (
        <PackOpeningModal
          cards={openingResult.cards}
          packName={openingResult.packName}
          onClose={() => setOpeningResult(null)}
        />
      )}

      {singleAcquired && (
        <SingleAcquireModal card={singleAcquired} onClose={() => setSingleAcquired(null)} />
      )}

      {isLoading && <div className="text-center text-white/40 py-12">Loading store…</div>}
    </div>
  );
}
